import React, { useMemo } from 'react';
import { SkeletonLine } from 'components/theme/Skeleton';
import { useIsMobile } from 'WindowDimensionProvider';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { DayPicker } from 'components/molecules/DayPicker';
import styled from 'styled-components';
import { IExperienceFields } from 'types/experience.types';
import { TYPE_RECURRING } from 'types/experience';
import { ExperiencesAvailability, Title4 } from './styles';

interface CalendarAvailabilityProps {
  experience: IExperienceFields;
  isDayHighlighted: (date: Date) => boolean;
  isNarrowView: boolean;
  onNextMonthClick: (date: Moment) => void;
  onPrevMonthClick: (date: Moment) => void;
  setSidebarFormValue: (field: string, value: Date) => void;
  startDateCalendar: Date | null;
  setSelectedSessionDateAvailabilityCalendar: (date: Moment) => void;
  firstAvailableDate: string | null;
  isCalendarLoading: boolean;
}

const CalendarAvailability: React.FC<CalendarAvailabilityProps> = ({
  experience,
  isDayHighlighted,
  isNarrowView,
  onNextMonthClick,
  onPrevMonthClick,
  setSidebarFormValue,
  startDateCalendar,
  setSelectedSessionDateAvailabilityCalendar,
  firstAvailableDate,
  isCalendarLoading,
}) => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();

  const defaultMonth = useMemo(() => {
    if (!firstAvailableDate) {
      return moment().startOf('month').toDate();
    }
    return moment(firstAvailableDate).startOf('month').toDate();
  }, [firstAvailableDate]);

  if (
    !experience.isBookable ||
    experience.type !== TYPE_RECURRING ||
    isMobile
  ) {
    return null;
  }

  if (experience.loading) {
    return <SkeletonLine />;
  }

  return (
    <ExperiencesAvailability
      role="region"
      aria-label={translate('dateRangeSelector')}
    >
      <div>
        <Title4
          as={'h3' as const}
          className="experience-view__experience-section-title"
        >
          {translate('avail')}
        </Title4>
        <CalendarAvailabilityWrapper>
          {startDateCalendar ? (
            <DayPicker
              mode="single"
              animate
              numberOfMonths={isMobile || isNarrowView ? 1 : 2}
              required
              selected={undefined}
              onSelect={(date) => {
                setSidebarFormValue('sessionDate', date);
                setSelectedSessionDateAvailabilityCalendar(moment(date));
              }}
              modifiers={{
                disabled: (date) => !isDayHighlighted(date),
              }}
              onNextClick={(date) => onNextMonthClick(moment(date))}
              onPrevClick={(date) => onPrevMonthClick(moment(date))}
              defaultMonth={defaultMonth}
              isLoading={isCalendarLoading}
            />
          ) : (
            <SkeletonLine />
          )}
        </CalendarAvailabilityWrapper>
      </div>
    </ExperiencesAvailability>
  );
};

const CalendarAvailabilityWrapper = styled.div`
  & > div {
    border: 0.5px solid var(--way-colors-borderColor);
    box-shadow: none !important;
  }
`;

export default CalendarAvailability;
